/**
 * EarthModel - Central state manager for earth rotation and projection
 * Phase 4.3: Projection Pipeline
 *
 * Ported from Python (map_projection/earth_model.py)
 */

import { mat3 } from 'gl-matrix';
import { createRotationMatrix, rotatePoints } from './rotation.js';
import { PROJECTIONS, RATIOS } from './projections.js';

export class EarthModel {
    constructor(countryData) {
        this.countryData = countryData;
        this.projection = 'mercator';

        // Current rotation angles (degrees)
        this.angles = {
            alpha: 0,  // Z-axis
            beta: 0,   // Y-axis
            gamma: 0,  // X-axis
        };

        // Rotation speeds (degrees per second)
        this.speeds = {
            alpha: 0,
            beta: 0,
            gamma: 0,
        };

        // Cached rotation matrix
        this.rotationMatrix = createRotationMatrix(0, 0, 0);

        // Canvas dimensions
        this.canvasWidth = 800;
        this.canvasHeight = 400;
    }

    /**
     * Set projection type
     * @param {string} projectionName - One of: mercator, miller, lambert, equirectangular
     */
    setProjection(projectionName) {
        if (!PROJECTIONS[projectionName]) {
            throw new Error(`Unknown projection: ${projectionName}`);
        }
        this.projection = projectionName;
    }

    /**
     * Update rotation angles
     * @param {number} alpha - Z-axis rotation (degrees)
     * @param {number} beta - Y-axis rotation (degrees)
     * @param {number} gamma - X-axis rotation (degrees)
     */
    setRotation(alpha, beta, gamma) {
        this.angles.alpha = alpha;
        this.angles.beta = beta;
        this.angles.gamma = gamma;

        // Recompute rotation matrix
        this.rotationMatrix = createRotationMatrix(alpha, beta, gamma);
    }

    /**
     * Update rotation speeds
     * @param {number} alphaSpeed - Z-axis speed (deg/sec)
     * @param {number} betaSpeed - Y-axis speed (deg/sec)
     * @param {number} gammaSpeed - X-axis speed (deg/sec)
     */
    setRotationSpeeds(alphaSpeed, betaSpeed, gammaSpeed) {
        this.speeds.alpha = alphaSpeed;
        this.speeds.beta = betaSpeed;
        this.speeds.gamma = gammaSpeed;
    }

    /**
     * Update angles based on time delta
     * @param {number} deltaTime - Time since last update (seconds)
     */
    update(deltaTime) {
        this.angles.alpha += this.speeds.alpha * deltaTime;
        this.angles.beta += this.speeds.beta * deltaTime;
        this.angles.gamma += this.speeds.gamma * deltaTime;

        // Wrap angles to [0, 360)
        this.angles.alpha = ((this.angles.alpha % 360) + 360) % 360;
        this.angles.beta = ((this.angles.beta % 360) + 360) % 360;
        this.angles.gamma = ((this.angles.gamma % 360) + 360) % 360;

        // Recompute rotation matrix
        this.rotationMatrix = createRotationMatrix(
            this.angles.alpha,
            this.angles.beta,
            this.angles.gamma
        );
    }

    /**
     * Reset rotation angles to zero (preserves rotation speeds)
     * Matches Python: earth_model.rotate(0, 0, 0)
     */
    resetRotation() {
        this.setRotation(0, 0, 0);
        // Don't reset speeds - only reset angles
    }

    /**
     * Apply incremental rotation from current state
     * Composes current rotation with delta rotation
     *
     * Ported from Python: earth_model_sprite.py lines 161-241 (angles_from_sequential_rotations)
     *
     * @param {number} deltaAlpha - Incremental Z-axis rotation (degrees)
     * @param {number} deltaBeta - Incremental Y-axis rotation (degrees)
     * @param {number} deltaGamma - Incremental X-axis rotation (degrees)
     */
    rotateFromState(deltaAlpha, deltaBeta, deltaGamma) {
        // Create delta rotation matrix
        const deltaMatrix = createRotationMatrix(deltaAlpha, deltaBeta, deltaGamma);

        // Compose: newMatrix = deltaMatrix * currentMatrix
        // IMPORTANT: Order matters! Apply delta rotation AFTER current rotation
        // Matches Python: R3 = R2 * R1 (lines 218-228 in earth_model_sprite.py)
        const newMatrix = mat3.create();
        mat3.multiply(newMatrix, deltaMatrix, this.rotationMatrix);

        this.rotationMatrix = newMatrix;

        // Extract Euler angles from composed matrix
        const angles = this._extractEulerAngles(newMatrix);
        this.angles.alpha = angles.alpha;
        this.angles.beta = angles.beta;
        this.angles.gamma = angles.gamma;
    }

    /**
     * Extract Euler angles from rotation matrix (Z-Y-X convention)
     * Ported from Python: earth_model_sprite.py lines 237-239
     *
     * @param {mat3} R - Rotation matrix (gl-matrix column-major format)
     * @returns {Object} {alpha, beta, gamma} in degrees [0, 360)
     * @private
     */
    _extractEulerAngles(R) {
        // gl-matrix uses column-major format:
        // [R00, R10, R20,
        //  R01, R11, R21,
        //  R02, R12, R22]

        // Extract elements for Z-Y-X Euler angle extraction
        const R20 = R[6];  // -sin(beta)
        const R21 = R[7];  // cos(beta)*sin(alpha)
        const R22 = R[8];  // cos(beta)*cos(alpha)
        const R10 = R[3];  // sin(gamma)*cos(beta)
        const R00 = R[0];  // cos(gamma)*cos(beta)

        // Extract angles (matching Python implementation)
        const beta = Math.asin(-R20);
        const alpha = Math.atan2(R21, R22);
        const gamma = Math.atan2(R10, R00);

        // Convert to degrees and normalize to [0, 360)
        let alphaDeg = (alpha * 180 / Math.PI + 360) % 360;
        let betaDeg = (beta * 180 / Math.PI + 360) % 360;
        let gammaDeg = (gamma * 180 / Math.PI + 360) % 360;

        return { alpha: alphaDeg, beta: betaDeg, gamma: gammaDeg };
    }

    /**
     * Set canvas dimensions (for coordinate scaling)
     */
    setCanvasDimensions(width, height) {
        this.canvasWidth = width;
        this.canvasHeight = height;
    }

    /**
     * Transform 3D sphere coordinates to 2D normalized coordinates
     * Applies rotation and projection (no screen scaling)
     *
     * @param {Float32Array} sphere3D - [x,y,z, ...] coordinates
     * @returns {Float32Array} [x,y, ...] normalized [0,1] coordinates
     */
    transformToNormalized2D(sphere3D) {
        // Step 1: Apply rotation
        const rotated = rotatePoints(sphere3D, this.rotationMatrix);

        // Step 2: Project to 2D using current projection
        const projectionFunc = PROJECTIONS[this.projection];
        const numVertices = rotated.length / 3;
        const projected2D = new Float32Array(numVertices * 2);

        for (let i = 0; i < numVertices; i++) {
            const x = rotated[i * 3];
            const y = rotated[i * 3 + 1];
            const z = rotated[i * 3 + 2];

            // Convert 3D Cartesian back to lat/lon
            // Clamp z to avoid NaN from Math.asin
            const zClamped = Math.max(-1, Math.min(1, z));
            const lat = Math.asin(zClamped);
            const lon = Math.atan2(y, x);

            // Apply projection
            const [px, py] = projectionFunc(lat, lon);

            projected2D[i * 2] = px;
            projected2D[i * 2 + 1] = py;
        }

        return projected2D;
    }

    /**
     * Transform pre-rotated 3D coordinates to 2D normalized coordinates
     * Performance optimization: skips rotation step when using cached rotations
     *
     * @param {Float32Array} rotated3D - [x,y,z, ...] pre-rotated coordinates
     * @returns {Float32Array} [x,y, ...] normalized [0,1] coordinates
     */
    transformRotatedToNormalized2D(rotated3D) {
        // Project to 2D using current projection (rotation already applied)
        const projectionFunc = PROJECTIONS[this.projection];
        const numVertices = rotated3D.length / 3;
        const projected2D = new Float32Array(numVertices * 2);

        for (let i = 0; i < numVertices; i++) {
            const x = rotated3D[i * 3];
            const y = rotated3D[i * 3 + 1];
            const z = rotated3D[i * 3 + 2];

            // Convert 3D Cartesian back to lat/lon
            const zClamped = Math.max(-1, Math.min(1, z));
            const lat = Math.asin(zClamped);
            const lon = Math.atan2(y, x);

            // Apply projection
            const [px, py] = projectionFunc(lat, lon);

            projected2D[i * 2] = px;
            projected2D[i * 2 + 1] = py;
        }

        return projected2D;
    }

    /**
     * Transform 3D sphere coordinates to 2D screen coordinates
     * Applies rotation, then projection, then screen space mapping
     *
     * @param {Float32Array} sphere3D - [x,y,z, ...] coordinates
     * @returns {Float32Array} [x,y, ...] screen coordinates
     */
    transformToScreen(sphere3D) {
        const normalized2D = this.transformToNormalized2D(sphere3D);

        // Scale to screen coordinates
        const screenCoords = new Float32Array(normalized2D.length);
        for (let i = 0; i < normalized2D.length; i += 2) {
            screenCoords[i] = normalized2D[i] * this.canvasWidth;
            screenCoords[i + 1] = normalized2D[i + 1] * this.canvasHeight;
        }

        return screenCoords;
    }

    /**
     * Transform normalized 2D coordinates to screen space
     * Used for paths that are already in 2D projection space
     *
     * @param {Float32Array} coords2D - [x,y, ...] normalized [0,1]
     * @param {number} width - Canvas width (defaults to this.canvasWidth)
     * @param {number} height - Canvas height (defaults to this.canvasHeight)
     * @returns {Float32Array} [x,y, ...] screen coordinates
     */
    transformNormalized2DToScreen(coords2D, width = this.canvasWidth, height = this.canvasHeight) {
        const screenCoords = new Float32Array(coords2D.length);
        for (let i = 0; i < coords2D.length; i += 2) {
            screenCoords[i] = coords2D[i] * width;
            screenCoords[i + 1] = coords2D[i + 1] * height;
        }
        return screenCoords;
    }

    /**
     * Get current aspect ratio for projection
     */
    getAspectRatio() {
        return RATIOS[this.projection];
    }

    /**
     * Get current rotation angles (for display)
     */
    getAngles() {
        return { ...this.angles };
    }

    /**
     * Get current rotation speeds (for display)
     */
    getSpeeds() {
        return { ...this.speeds };
    }
}
